import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ApiError } from '../lib/api/apiError';
import { wasProjectRecentlyDeleted } from '../lib/recentlyDeletedProjects';

/**
 * True only for a genuine "this project is gone" 404. `fetchApi` throws an
 * `ApiError` (with `.status`) for HTTP errors, but a transient network failure
 * throws a friendly plain `Error` instead — so a laptop-wake blip never matches
 * and the user is only bounced when the project has really 404'd.
 */
export function isProjectNotFound(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404;
}

/**
 * Recovery for a deleted / non-existent project in the URL.
 *
 * When a project-scoped page's fetch returns 404, redirect off the dead route
 * once (replacing history so Back doesn't return to it) and show a toast, instead
 * of stranding the user on a retrying error card. Users can then delete any
 * project — including the seeded AURA demo — without landing in a 404 loop.
 *
 * See incident 2026-07-13: a real signup deleted their demo while its issues
 * page sat open in a tab; the tab re-404'd on every refocus/retry/SSE reconnect
 * because nothing cleared the dead route (memory/debug_log.md).
 *
 * `projectId` (the id in the current URL) lets the recovery tell an intentional
 * self-delete apart from a stale tab. When the current session just deleted this
 * project (its delete handler already navigates and shows "Project deleted"),
 * the same 404 fires — from the mounted useProject(id) query being refetched by
 * the delete's ['projects'] invalidation — but the "This project no longer
 * exists." toast would then contradict the success toast and read like a
 * dead-link click. In that case we still redirect (safety net) but suppress the
 * toast. A stale tab (deleted elsewhere) is NOT in this session's just-deleted
 * set, so it keeps the original recovery toast.
 */
export function useProjectNotFoundRedirect(
  error: unknown,
  redirectTo = '/projects',
  projectId?: string,
): void {
  const navigate = useNavigate();
  const firedRef = useRef(false);

  useEffect(() => {
    if (firedRef.current) return;
    if (!isProjectNotFound(error)) return;
    firedRef.current = true;
    if (!wasProjectRecentlyDeleted(projectId)) {
      toast.info('This project no longer exists.');
    }
    navigate(redirectTo, { replace: true });
  }, [error, navigate, redirectTo, projectId]);
}
